import { useCallback, useEffect, useState } from "react"
import api from "../../utils/api"
import Icon from "../Icon"
import { SkeletonTable } from "../Skeleton"
import { formatMoney } from "../../utils/format"
import { getApiErrorMessage } from "../../utils/errorMessage"
import Modal from "../../pages/utbills/Modal"
import StatementTable from "../../pages/utbills/StatementTable"
import { getPreviousAndCurrentMonthRange } from "../../pages/frp/frpHelpers"
import {
  getActivityContext,
  getActivityDescription,
  getActivityLabel,
  getActivityPayer,
  getAmountDisplay,
  getDateLines,
  getOwedDisplay,
  getWalletDisplay,
} from "./allTransactionsDisplay"

// A long history opens on the newest records; the reader walks back a page at a time.
const PAGE_SIZE = 10

const TONE_CLASSES = {
  in: "font-semibold text-emerald-700",
  out: "font-semibold text-red-600",
  bill: "font-semibold text-slate-700",
  owed: "font-semibold text-red-600",
  credit: "font-semibold text-emerald-700",
  plain: "font-semibold text-slate-700",
  none: "text-slate-400",
}

function Figure({ display }) {
  return <span className={TONE_CLASSES[display.tone] || TONE_CLASSES.plain}>{display.text}</span>
}

function HeadlineFigure({ label, value, tone }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
      <p className="text-[9px] font-bold uppercase tracking-wide text-slate-400">{label}</p>
      <p className={`mt-0.5 text-sm font-black leading-tight tabular-nums ${tone}`}>{formatMoney(value)}</p>
    </div>
  )
}

function ActivityDetails({ row }) {
  const description = getActivityDescription(row)
  const context = getActivityContext(row)
  const payer = getActivityPayer(row)
  return (
    <div>
      <div className="font-bold text-slate-900">{getActivityLabel(row)}</div>
      {description && <div className="mt-0.5 text-slate-600">{description}</div>}
      {context && <div className="mt-0.5 text-slate-400">{context}</div>}
      {(row.reference || payer) && (
        // The payer sits after the Ref; on a narrow phone it drops to the next line.
        <div className="mt-0.5 flex flex-wrap gap-x-2 font-medium text-slate-500">
          {row.reference && <span>Ref {row.reference}</span>}
          {payer && <span className="min-w-0 break-words">{payer}</span>}
        </div>
      )}
    </div>
  )
}

// Widths checked at 360px: a narrow stacked date leaves the three money columns
// room for amounts up to 999,999.99 on one line.
const COLUMNS = [
  {
    label: "Date",
    width: "13%",
    render: (row) => getDateLines(row.date).map((line) => <div key={line}>{line}</div>),
  },
  { label: "Details", align: "left", render: (row) => <ActivityDetails row={row} /> },
  { label: "Amount", width: "19%", align: "right", render: (row) => <Figure display={getAmountDisplay(row)} /> },
  { label: "Wallet", width: "19%", align: "right", render: (row) => <Figure display={getWalletDisplay(row.walletBalanceAfter)} /> },
  { label: "You owe", width: "19%", align: "right", render: (row) => <Figure display={getOwedDisplay(row.amountOwedAfter)} /> },
]

// One history of the signed-in person's own money in one module (Rentals,
// Bills & Invoices or Groups): wallet top-ups, payments, income and transfers,
// plus the bills raised to them. `config` comes from allTransactionsConfigs.js.
function AllTransactionsModal({ config, onClose }) {
  const { endpoint, subtitle, owedLabel, noSignNote, owedNote } = config
  const [activity, setActivity] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [range, setRange] = useState(getPreviousAndCurrentMonthRange)
  const [draftRange, setDraftRange] = useState(range)
  const [filterOpen, setFilterOpen] = useState(false)
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)

  const loadActivity = useCallback(async ({ start, end }) => {
    setLoading(true)
    setError("")
    setVisibleCount(PAGE_SIZE)
    try {
      const params = new URLSearchParams()
      if (start) params.append("startDate", start)
      if (end) params.append("endDate", end)
      const response = await api.get(`${endpoint}?${params.toString()}`)
      setActivity(response?.data || null)
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to load your transactions."))
    } finally {
      setLoading(false)
    }
  }, [endpoint])

  useEffect(() => {
    loadActivity(range)
  }, [loadActivity, range])

  const applyFilter = () => setRange({ ...draftRange })
  const resetFilter = () => {
    const defaults = getPreviousAndCurrentMonthRange()
    setDraftRange(defaults)
    setRange(defaults)
  }

  const rows = Array.isArray(activity?.rows) ? activity.rows : []
  const shownRows = rows.slice(0, visibleCount)
  const hiddenCount = rows.length - shownRows.length
  const amountOwed = Number(activity?.amountOwed || 0)
  // Rentals sends hasRentLedger; Bills & Invoices and Groups send hasDebtLedger.
  const showOwed = Boolean(activity?.hasDebtLedger ?? activity?.hasRentLedger)

  return (
    <Modal
      title="All Transactions"
      subtitle={subtitle}
      icon="clock-history"
      size="wide"
      onClose={onClose}
    >
      {loading ? (
        <div className="grid grid-cols-2 gap-2">
          {[0, 1].map((index) => (
            <div key={index} className="h-[50px] animate-pulse rounded-xl bg-slate-100" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <HeadlineFigure label="Wallet balance" value={activity?.walletBalance} tone="text-slate-900" />
          {showOwed && (
            <HeadlineFigure
              label={owedLabel}
              value={amountOwed}
              tone={amountOwed > 0 ? "text-rose-600" : "text-emerald-700"}
            />
          )}
        </div>
      )}

      <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
        <button
          type="button"
          onClick={() => setFilterOpen((open) => !open)}
          aria-expanded={filterOpen}
          className="flex w-full items-center justify-between text-xs font-semibold text-slate-700"
        >
          <span className="flex items-center gap-1.5">
            <Icon name="calendar3" size="0.8rem" className="text-slate-500" />
            {range.start || "Start"} to {range.end || "today"}
          </span>
          <Icon name={filterOpen ? "chevron-up" : "chevron-down"} size="0.8rem" className="text-slate-500" />
        </button>
        {filterOpen && (
          <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-[1fr_1fr_auto_auto] sm:items-end">
            <label className="text-[11px] font-medium text-slate-600">
              Start date
              <input
                type="date"
                value={draftRange.start}
                onChange={(event) => setDraftRange((current) => ({ ...current, start: event.target.value }))}
                className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm"
              />
            </label>
            <label className="text-[11px] font-medium text-slate-600">
              End date
              <input
                type="date"
                value={draftRange.end}
                onChange={(event) => setDraftRange((current) => ({ ...current, end: event.target.value }))}
                className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm"
              />
            </label>
            <button
              type="button"
              onClick={applyFilter}
              className="rounded-lg bg-emerald-600 px-3 py-2 text-xs font-bold text-white transition-colors hover:bg-emerald-700"
            >
              Show
            </button>
            <button
              type="button"
              onClick={resetFilter}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-bold text-slate-600 transition-colors hover:bg-slate-100"
            >
              Last 2 months
            </button>
          </div>
        )}
      </div>

      {error && (
        <p className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-700">{error}</p>
      )}

      <div className="-mx-2 mt-3 overflow-hidden rounded-xl sm:mx-0">
        {loading ? (
          <SkeletonTable rows={6} columns={5} />
        ) : (
          !error && (
            <StatementTable
              rows={shownRows}
              rowKey={(row) => row.id}
              emptyText="No activity in this period."
              columns={COLUMNS}
            />
          )
        )}
      </div>

      {!loading && !error && rows.length > 0 && (
        <p className="mt-2 text-[10px] leading-snug text-slate-500 sm:text-xs">
          <span className="font-semibold text-emerald-700">+</span> money came into your wallet.{" "}
          <span className="font-semibold text-red-600">-</span> money left your wallet. {noSignNote} {owedNote}
        </p>
      )}

      {!loading && (hiddenCount > 0 || visibleCount > PAGE_SIZE) && (
        <div className="mt-3 flex items-center justify-center gap-2">
          {hiddenCount > 0 && (
            <button
              type="button"
              onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-white px-3 py-1.5 text-xs font-bold text-emerald-700 transition-colors hover:bg-emerald-50"
            >
              <Icon name="chevron-down" size="0.65rem" />
              See {Math.min(PAGE_SIZE, hiddenCount)} more
            </button>
          )}
          {visibleCount > PAGE_SIZE && (
            <button
              type="button"
              onClick={() => setVisibleCount(PAGE_SIZE)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-600 transition-colors hover:bg-slate-100"
            >
              <Icon name="chevron-up" size="0.65rem" />
              Show less
            </button>
          )}
        </div>
      )}
    </Modal>
  )
}

export default AllTransactionsModal
